import { useQuery } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { Link } from 'react-router-dom';
import { Plus, Building2, Users, FolderKanban, Search, Sparkles, ArrowUpRight } from 'lucide-react';
import { useState, useEffect } from 'react';
import { usePageHeaderStore } from '../../stores/pageHeader';

interface Organization {
  id: string;
  name: string;
  slug: string;
  status: string;
  memberCount?: number;
  projectCount?: number;
  createdAt: string;
}

export function OrganizationsPage() {
  const [search, setSearch] = useState('');
  const { setHeader, clearHeader } = usePageHeaderStore();
  
  useEffect(() => {
    setHeader(
      'Organizations',
      'Manage all organizations in the system',
      <Building2 className="w-5 h-5" style={{ color: 'var(--primary)' }} />,
      undefined,
      {
        actions: [
          {
            id: 'new-org',
            label: 'New Organization',
            icon: <Sparkles className="w-4 h-4" />,
            variant: 'primary',
            onClick: () => {
              // TODO: Implement new org creation
              console.log('Create new organization');
            },
          },
        ],
      }
    );
    return () => clearHeader();
  }, [setHeader, clearHeader]);
  
  const { data, isLoading } = useQuery({
    queryKey: ['orgs', search],
    queryFn: () =>
      api.get<{ data: Organization[]; pagination: { total: number } }>(
        `/orgs?search=${encodeURIComponent(search)}`
      ),
  });
  
  return (
    <div className="animate-fade-in">
      {/* Search */}
      <div className="relative max-w-md mb-6">
        <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-subtle" />
        <input
          type="text"
          placeholder="Search organizations..."
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="input pl-10"
        />
      </div>
      
      {/* Organizations grid */}
      {isLoading ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <div key={i} className="card">
              <div className="skeleton h-6 w-3/4 mb-4" />
              <div className="skeleton h-4 w-1/2" />
            </div>
          ))}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {data?.data.map((org, index) => (
            <Link
              key={org.id}
              to={`/app/organizations/${org.id}`}
              className="card group hover:border-primary transition-all duration-200"
              style={{ animationDelay: `${index * 50}ms` }}
            >
              <div className="flex items-start justify-between">
                <div className="w-10 h-10 rounded-card flex items-center justify-center"
                  style={{ background: 'var(--primary-dim)' }}
                >
                  <Building2 className="w-5 h-5" style={{ color: 'var(--primary)' }} />
                </div>
                <span className={`badge ${org.status === 'active' ? 'badge-success' : 'badge-warning'}`}>
                  {org.status}
                </span>
              </div>
              <h3 className="mt-4 text-lg font-semibold text-default group-hover:text-primary transition-colors">
                {org.name}
              </h3>
              <p className="text-sm text-muted mt-1">
                @{org.slug}
              </p>
              <div className="divider" />
              <div className="flex items-center gap-4 text-sm text-muted">
                <span className="flex items-center gap-1.5">
                  <FolderKanban className="w-4 h-4" />
                  {org.projectCount ?? 0} projects
                </span>
                <span className="flex items-center gap-1.5">
                  <Users className="w-4 h-4" />
                  {org.memberCount ?? 0} members
                </span>
              </div>
              <ArrowUpRight className="absolute top-4 right-4 w-4 h-4 text-subtle opacity-0 group-hover:opacity-100 transition-opacity" />
            </Link>
          ))}
          
          {data?.data.length === 0 && (
            <div className="col-span-full empty-state">
              <Building2 className="empty-state-icon" />
              <p className="empty-state-title">No organizations found</p>
              <p className="empty-state-text">
                {search ? 'Try a different search term' : 'Create your first organization to get started'}
              </p>
              {!search && (
                <button className="btn btn-primary">
                  <Plus className="w-4 h-4" />
                  Create Organization
                </button>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
